import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Truck, Trophy, ShieldCheck } from "lucide-react";

/**
 * Homepage audience section — "One platform. Different advantages."
 * Three alternating full-width feature rows + a closing statement bar.
 * All artwork is hand-built inline SVG (no product UI, no raster images).
 */

const TEAL = "#02857E";
const TEAL_LIGHT = "#0FA88E";

/* ── shared bits ──────────────────────────────────────────────── */

const LRMark: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`} stroke={TEAL} strokeWidth="2.1" fill="none" strokeLinecap="round">
    <path d="M0 0v17h10" />
    <path d="M9 -3v17h10" opacity="0.55" />
  </g>
);

const Avatar: React.FC<{ x: number; y: number; r?: number }> = ({ x, y, r = 11 }) => (
  <g transform={`translate(${x} ${y})`}>
    <circle r={r} fill="#EAF3F2" />
    <circle cy={-r * 0.22} r={r * 0.3} fill={TEAL} opacity="0.85" />
    <path
      d={`M${-r * 0.52} ${r * 0.5}a${r * 0.52} ${r * 0.44} 0 0 1 ${r * 1.04} 0z`}
      fill={TEAL}
      opacity="0.85"
    />
  </g>
);

const TruckGlyph: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g
    transform={`translate(${x} ${y}) scale(${s})`}
    fill="none"
    stroke={TEAL}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M-11 4V-5h13v9z" />
    <path d="M2 4h6l4-4.5V-2H2z" />
    <circle cx="-5" cy="6.4" r="2" />
    <circle cx="7" cy="6.4" r="2" />
  </g>
);

const Building: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <rect x="-16" y="-14" width="15" height="28" rx="1.5" fill="#3F5A63" />
    <rect x="-1" y="-4" width="18" height="18" rx="1.5" fill="#8FA5AC" />
    <g fill="#FFFFFF" opacity="0.7">
      {[-11, -6].map((cx) =>
        [-9, -3, 3].map((cy) => <rect key={`${cx}${cy}`} x={cx} y={cy} width="3" height="3" rx="0.5" />),
      )}
      {[2, 7, 12].map((cx) =>
        [0, 6].map((cy) => <rect key={`b${cx}${cy}`} x={cx} y={cy} width="3.5" height="3.5" rx="0.5" />),
      )}
    </g>
    <path d="M20 -18v32" stroke="#3F5A63" strokeWidth="1.4" />
    <path d="M20 -18l-7 3 7 3z" fill={TEAL} opacity="0.85" />
  </g>
);

/* ── Row 1 artwork: controlled network hub ─────────────────────── */

const ShipperArt = (
  <svg viewBox="0 0 620 350" className="h-auto w-full" fill="none" aria-hidden>
    <defs>
      <filter id="wifSoft" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#0E1B1A" floodOpacity="0.10" />
      </filter>
    </defs>

    {/* orbit rings around the hub */}
    <circle cx="248" cy="176" r="118" stroke={TEAL} strokeOpacity="0.13" strokeDasharray="3 6" />
    <circle cx="248" cy="176" r="92" stroke={TEAL} strokeOpacity="0.10" strokeDasharray="3 6" />

    {/* dashed fan-out to broker / carrier panels */}
    <g stroke={TEAL_LIGHT} strokeOpacity="0.5" strokeWidth="1.2" strokeDasharray="4 5">
      <path d="M316 148C374 138 388 92 436 88" />
      <path d="M320 162C378 158 392 128 436 124" />
      <path d="M322 176C380 178 394 160 436 160" />
      <path d="M320 192C378 206 392 224 436 226" />
      <path d="M314 206C372 232 388 258 436 260" />
      <path d="M306 218C356 258 386 292 436 294" />
    </g>
    <g fill={TEAL_LIGHT} opacity="0.75">
      {[
        [372, 118],
        [366, 152],
        [370, 177],
        [366, 205],
        [364, 240],
        [352, 262],
      ].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2.4" />
      ))}
    </g>

    {/* shipper node */}
    <g filter="url(#wifSoft)">
      <circle cx="62" cy="176" r="46" fill="#FFFFFF" />
    </g>
    <circle cx="62" cy="176" r="46" stroke="#E3EBEE" />
    <Building x={62} y={170} s={0.86} />
    <text
      x="62"
      y="206"
      textAnchor="middle"
      fontSize="8.5"
      fontWeight="700"
      letterSpacing="1.1"
      fill="#5B6B70"
    >
      SHIPPER
    </text>

    {/* shipper → hub */}
    <path d="M110 176h62" stroke={TEAL} strokeWidth="2" />
    <path d="M166 171l7 5-7 5z" fill={TEAL} />
    <circle cx="112" cy="176" r="3.4" fill={TEAL} />

    {/* hub */}
    <g filter="url(#wifSoft)">
      <circle cx="248" cy="176" r="66" fill="#FFFFFF" />
    </g>
    <circle cx="248" cy="176" r="66" stroke={TEAL} strokeOpacity="0.55" strokeWidth="1.4" />
    <LRMark x={239} y={144} s={1.05} />
    <text x="248" y="184" textAnchor="middle" fontSize="13.5" fontWeight="700" fill="#0E1B1A">
      LaneRunner
    </text>
    <text
      x="248"
      y="199"
      textAnchor="middle"
      fontSize="8.6"
      fontWeight="700"
      letterSpacing="0.9"
      fill={TEAL}
    >
      CONTROLLED
    </text>
    <text
      x="248"
      y="211"
      textAnchor="middle"
      fontSize="8.6"
      fontWeight="700"
      letterSpacing="0.9"
      fill={TEAL}
    >
      NETWORK
    </text>

    {/* vetted brokers panel */}
    <g filter="url(#wifSoft)">
      <rect x="436" y="52" width="104" height="150" rx="12" fill="#FFFFFF" />
    </g>
    <rect x="436" y="52" width="104" height="150" rx="12" stroke="#E7EEF0" />
    <text x="452" y="74" fontSize="8.2" fontWeight="700" letterSpacing="1" fill="#5B6B70">
      VETTED BROKERS
    </text>
    {[104, 140, 176].map((cy) => (
      <g key={`b${cy}`}>
        <circle cx="488" cy={cy} r="15" fill="#FFFFFF" stroke="#E7EEF0" />
        <Avatar x={488} y={cy} r={9} />
      </g>
    ))}

    {/* vetted carriers panel */}
    <g filter="url(#wifSoft)">
      <rect x="436" y="216" width="104" height="122" rx="12" fill="#FFFFFF" />
    </g>
    <rect x="436" y="216" width="104" height="122" rx="12" stroke="#E7EEF0" />
    <text x="452" y="238" fontSize="8.2" fontWeight="700" letterSpacing="1" fill={TEAL}>
      VETTED CARRIERS
    </text>
    {[262, 296, 322].map((cy) => (
      <g key={`c${cy}`}>
        <circle cx="488" cy={cy} r="14" fill="#FFFFFF" stroke="#E7EEF0" />
        <TruckGlyph x={488} y={cy} s={0.82} />
      </g>
    ))}
  </svg>
);

/* ── Row 2 artwork: controlled bidding arena ───────────────────── */

const Bubble: React.FC<{
  x: number;
  y: number;
  name: string;
  amount: string;
  active?: boolean;
}> = ({ x, y, name, amount, active }) => (
  <g>
    <circle
      cx={x}
      cy={y}
      r="34"
      fill="#FFFFFF"
      stroke={active ? TEAL : "#E4ECEE"}
      strokeWidth={active ? 1.6 : 1}
      filter="url(#arenaSoft)"
    />
    <Avatar x={x} y={y - 14} r={9} />
    <text
      x={x}
      y={y + 8}
      textAnchor="middle"
      fontSize="9.4"
      fill={active ? TEAL : "#3D4C50"}
      fontWeight="500"
    >
      {name}
    </text>
    <text
      x={x}
      y={y + 21}
      textAnchor="middle"
      fontSize="10.4"
      fontWeight="700"
      fill={active ? TEAL : "#0E1B1A"}
    >
      {amount}
    </text>
  </g>
);

const BrokerArt = (
  <svg viewBox="0 0 620 400" className="h-auto w-full" fill="none" aria-hidden>
    <defs>
      <filter id="arenaSoft" x="-40%" y="-40%" width="180%" height="180%">
        <feDropShadow dx="0" dy="8" stdDeviation="9" floodColor="#0E1B1A" floodOpacity="0.10" />
      </filter>
      <linearGradient id="arenaTop" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#EAF1F1" />
      </linearGradient>
      <linearGradient id="arenaSide" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#7FA3A5" />
        <stop offset="50%" stopColor="#A9C3C3" />
        <stop offset="100%" stopColor="#7FA3A5" />
      </linearGradient>
    </defs>

    {/* isometric platform */}
    <path
      d="M110 292a200 74 0 0 0 400 0v-24a200 74 0 0 1-400 0z"
      fill="url(#arenaSide)"
      opacity="0.85"
    />
    <ellipse cx="310" cy="268" rx="200" ry="74" fill="url(#arenaTop)" />
    <ellipse cx="310" cy="268" rx="200" ry="74" stroke="#DCE7E7" />
    <ellipse cx="310" cy="266" rx="164" ry="58" stroke="#E6EEEE" />

    {/* steps */}
    <g fill="#FFFFFF" stroke="#DFE9E9">
      <rect x="270" y="330" width="80" height="10" rx="2" />
      <rect x="262" y="340" width="96" height="10" rx="2" />
      <rect x="254" y="350" width="112" height="10" rx="2" />
    </g>

    {/* ring posts + ropes */}
    <g stroke="#A9C3C3" strokeWidth="2.4" strokeLinecap="round">
      <path d="M126 250v50M494 250v50M214 214v46M406 214v46" />
    </g>
    <g stroke="#C6D8D8" strokeWidth="1.6">
      <path d="M126 258l88-32M494 258l-88-32M126 272l88-32M494 272l-88-32M126 286l88-32M494 286l-88-32" />
    </g>

    {/* dashed bid arrows toward the centre */}
    <g stroke={TEAL} strokeOpacity="0.45" strokeWidth="1.2" strokeDasharray="4 5">
      <path d="M310 152v56" />
      <path d="M228 190l52 24" />
      <path d="M392 190l-52 24" />
      <path d="M244 300l40-24" />
      <path d="M376 300l-40-24" />
    </g>
    <g fill={TEAL} opacity="0.55">
      <path d="M305 208l5 8 5-8z" />
      <path d="M276 210l10 5-3-10z" />
      <path d="M344 210l-10 5 3-10z" />
      <path d="M282 272l-10 4 8 6z" />
      <path d="M338 272l10 4-8 6z" />
    </g>

    {/* awarded centre */}
    <circle cx="310" cy="248" r="30" fill="#FFFFFF" stroke={TEAL} strokeWidth="1.8" filter="url(#arenaSoft)" />
    <g transform="translate(310 248)" stroke={TEAL} strokeWidth="1.7" fill="none" strokeLinecap="round">
      <path d="M-7 -9h14v5a7 7 0 0 1-14 0z" />
      <path d="M-7 -7h-3a4 4 0 0 0 4 4M7 -7h3a4 4 0 0 1-4 4" />
      <path d="M0 1v5M-5 8h10" />
    </g>
    <text
      x="310"
      y="298"
      textAnchor="middle"
      fontSize="9"
      fontWeight="700"
      letterSpacing="1"
      fill="#5B6B70"
    >
      AWARDED
    </text>
    <text x="310" y="316" textAnchor="middle" fontSize="14" fontWeight="700" fill="#0E1B1A">
      $2,850
    </text>

    <Bubble x={310} y={118} name="Broker A" amount="$2,900" />
    <Bubble x={188} y={166} name="Broker B" amount="$3,020" />
    <Bubble x={434} y={172} name="Broker C" amount="$2,760" active />
    <Bubble x={206} y={318} name="Broker D" amount="$2,940" />
    <Bubble x={422} y={322} name="Broker E" amount="$3,100" />
  </svg>
);

/* ── Row 3 artwork: direct access map ─────────────────────────── */

const Pin: React.FC<{ x: number; y: number }> = ({ x, y }) => (
  <g transform={`translate(${x} ${y})`}>
    <path d="M0 12c0 0 9-10 9-17A9 9 0 1 0-9-5c0 7 9 17 9 17z" fill={TEAL} />
    <circle cy="-5" r="3.2" fill="#FFFFFF" />
  </g>
);

const Tree: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`} stroke="#CBD8D8" strokeWidth="1.1" fill="none">
    <path d="M0 10V-2" />
    <path d="M-6 2l6-10 6 10z" />
  </g>
);

const CarrierArt = (
  <svg viewBox="0 0 620 350" className="h-auto w-full" fill="none" aria-hidden>
    <defs>
      <filter id="mapSoft" x="-40%" y="-40%" width="180%" height="180%">
        <feDropShadow dx="0" dy="8" stdDeviation="9" floodColor="#0E1B1A" floodOpacity="0.10" />
      </filter>
    </defs>

    {/* faint road network */}
    <g stroke="#DCE5E6" strokeWidth="1.3">
      <path d="M0 96C90 84 150 130 236 116S372 62 470 74s110 26 150 18" />
      <path d="M-10 176C80 176 130 200 210 196s150-48 240-44 130 40 190 34" />
      <path d="M0 260C96 244 150 288 250 280s140-40 230-34 130 22 140 20" />
      <path d="M120 20v310M330 10v330M470 30v300" strokeOpacity="0.55" />
    </g>
    <g stroke="#D5E0E1" strokeWidth="1.2" strokeDasharray="5 6">
      <path d="M30 316C110 300 140 250 220 246" />
      <path d="M520 300C470 286 466 258 500 236" />
    </g>
    {[
      [62, 70],
      [176, 60],
      [246, 232],
      [90, 250],
      [402, 60],
      [356, 246],
      [536, 208],
      [150, 300],
      [300, 82],
    ].map(([x, y], i) => (
      <Tree key={`${x}-${y}`} x={x} y={y} s={i % 3 === 0 ? 1.15 : 0.9} />
    ))}

    {/* shipper node */}
    <circle cx="62" cy="176" r="44" fill="#FFFFFF" filter="url(#mapSoft)" />
    <circle cx="62" cy="176" r="44" stroke="#E3EBEE" />
    <Building x={62} y={170} s={0.8} />
    <text
      x="62"
      y="204"
      textAnchor="middle"
      fontSize="8.2"
      fontWeight="700"
      letterSpacing="1.1"
      fill="#5B6B70"
    >
      SHIPPER
    </text>

    <path d="M108 176h56" stroke={TEAL} strokeWidth="2.2" />
    <path d="M158 171l7 5-7 5z" fill={TEAL} />

    {/* hub */}
    <circle cx="234" cy="176" r="68" fill="#FFFFFF" filter="url(#mapSoft)" />
    <circle cx="234" cy="176" r="68" stroke={TEAL} strokeOpacity="0.55" strokeWidth="1.4" />
    <LRMark x={225} y={142} s={1.05} />
    <text x="234" y="184" textAnchor="middle" fontSize="13.5" fontWeight="700" fill="#0E1B1A">
      LaneRunner
    </text>
    <text
      x="234"
      y="200"
      textAnchor="middle"
      fontSize="8.4"
      fontWeight="700"
      letterSpacing="0.9"
      fill={TEAL}
    >
      DIRECT ACCESS
    </text>
    <text
      x="234"
      y="212"
      textAnchor="middle"
      fontSize="8.4"
      fontWeight="700"
      letterSpacing="0.9"
      fill={TEAL}
    >
      TO OPPORTUNITIES
    </text>

    {/* thick teal lanes to trucks */}
    <g stroke={TEAL} strokeWidth="3.2" strokeLinecap="round">
      <path d="M296 148C346 132 372 106 418 100" />
      <path d="M302 176h130" />
      <path d="M296 206C348 226 366 250 412 258" />
    </g>

    {/* trucks */}
    {[
      [446, 96],
      [462, 176],
      [438, 262],
    ].map(([cx, cy]) => (
      <g key={`t${cx}-${cy}`}>
        <circle cx={cx} cy={cy} r="24" fill="#FFFFFF" filter="url(#mapSoft)" />
        <circle cx={cx} cy={cy} r="24" stroke="#E7EEF0" />
        <TruckGlyph x={cx} y={cy} s={1.15} />
      </g>
    ))}

    {/* dashed last-mile to pins */}
    <g stroke={TEAL} strokeOpacity="0.5" strokeWidth="1.4" strokeDasharray="5 5">
      <path d="M470 92C500 86 510 66 526 60" />
      <path d="M486 172C512 168 522 148 536 140" />
      <path d="M462 258C492 258 506 240 518 232" />
    </g>
    <Pin x={534} y={44} />
    <Pin x={548} y={124} />
    <Pin x={528} y={216} />
  </svg>
);

/* ── rows ─────────────────────────────────────────────────────── */

type Row = {
  key: string;
  label: string;
  icon: React.ReactNode;
  headline: string;
  body: string;
  emphasis: string;
  emphasisPill?: boolean;
  secondary?: string;
  cta: string;
  href: string;
  visual: React.ReactNode;
  visualFirst: boolean;
};

const ROWS: Row[] = [
  {
    key: "shippers",
    label: "SHIPPERS",
    icon: null,
    headline: "Capacity without the chase.",
    body: "Reach vetted brokers and carriers from one place. Start with the partners you already trust, open freight to additional capacity when needed, compare quotes clearly, and award with confidence.",
    emphasis: "100% capacity. Faster.",
    cta: "For shippers",
    href: "/shippers",
    visual: ShipperArt,
    visualFirst: true,
  },
  {
    key: "brokers",
    label: "BROKERS",
    icon: <Users className="h-[18px] w-[18px]" />,
    headline: "Stop cold calling. Start winning freight.",
    body: "Get in front of shippers through the freight they actually need moved. Bid on opportunities that fit your network, win loads, perform, and turn those awards into long-term customer relationships.",
    emphasis: "Better competition. Not endless competition.",
    emphasisPill: true,
    secondary:
      "Controlled participation. Capped bidding. Broker opportunities stay broker opportunities — so you compete on service, capacity, and rate, not in a race to the bottom.",
    cta: "For brokers",
    href: "/brokers",
    visual: BrokerArt,
    visualFirst: false,
  },
  {
    key: "carriers",
    label: "CARRIERS",
    icon: <Truck className="h-[18px] w-[18px]" />,
    headline: "Get closer to the freight.",
    body: "Access shipper-posted loads and contract opportunities that fit your equipment and lanes. Compete in controlled bidding environments and build direct relationships without unnecessary layers between you and the shipper.",
    emphasis: "More direct freight. Fewer middlemen.",
    cta: "For carriers",
    href: "/carriers",
    visual: CarrierArt,
    visualFirst: true,
  },
];

const HomeWhoItsFor: React.FC = () => (
  <section className="bg-[#FBFCFC] py-20 sm:py-24">
    <div className="mx-auto max-w-[1540px] px-5 sm:px-8">
      {/* intro */}
      <div className="mx-auto max-w-3xl text-center">
        <span className="text-[11.5px] font-bold uppercase tracking-[0.2em] text-[#0B6B62]">
          Built for every side of freight
        </span>
        <h2 className="mt-4 text-[34px] font-bold leading-[1.06] tracking-[-0.02em] text-[#0E1B1A] sm:text-[46px]">
          One platform. Different advantages.
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-[15.5px] leading-relaxed text-[#54646A]">
          Shippers get capacity. Brokers get opportunity. Carriers get closer to the freight.
        </p>
      </div>

      {/* rows */}
      <div className="mt-12 space-y-6 sm:mt-14">
        {ROWS.map((r) => {
          const textCol = (
            <div className={r.visualFirst ? "lg:order-2 lg:pl-2" : "lg:order-1 lg:pr-2"}>
              <span className="inline-flex items-center gap-2.5 text-[11.5px] font-bold uppercase tracking-[0.16em] text-[#0B6B62]">
                {r.icon}
                {r.label}
              </span>
              <h3 className="mt-3.5 text-[26px] font-bold leading-[1.14] tracking-[-0.02em] text-[#0E1B1A] sm:text-[30px]">
                {r.headline}
              </h3>
              <p className="mt-4 text-[14.5px] leading-[1.72] text-[#5C6C71]">{r.body}</p>

              {r.emphasisPill ? (
                <div className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-[#02857E]/[0.07] px-4 py-2.5">
                  <Trophy className="h-[15px] w-[15px] shrink-0 text-[#02857E]" />
                  <span className="text-[14px] font-bold text-[#0E1B1A]">{r.emphasis}</span>
                </div>
              ) : (
                <p className="mt-5 text-[15.5px] font-bold text-[#0E1B1A]">{r.emphasis}</p>
              )}

              {r.secondary ? (
                <p className="mt-4 text-[14.5px] leading-[1.72] text-[#5C6C71]">{r.secondary}</p>
              ) : null}

              <Link
                to={r.href}
                className="mt-6 inline-flex items-center gap-2 text-[14.5px] font-semibold text-[#02857E] transition hover:gap-3 hover:underline"
              >
                {r.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          );

          const visualCol = (
            <div
              className={`flex items-center justify-center ${
                r.visualFirst ? "lg:order-1" : "lg:order-2"
              }`}
            >
              <div className="w-full">{r.visual}</div>
            </div>
          );

          return (
            <div
              key={r.key}
              className="rounded-[20px] border border-[#EAF0F0] bg-gradient-to-br from-[#FCFDFD] to-[#F4F8F7] px-6 py-9 shadow-[0_10px_30px_-26px_rgba(15,42,50,0.35)] sm:px-9 sm:py-10 lg:px-11"
            >
              <div className="grid grid-cols-1 items-center gap-9 lg:grid-cols-[minmax(0,1.06fr)_minmax(0,0.94fr)] lg:gap-12">
                {visualCol}
                {textCol}
              </div>
            </div>
          );
        })}
      </div>

      {/* statement bar */}
      <div className="relative mt-6 overflow-hidden rounded-[20px] border border-[#E4EEEC] bg-gradient-to-r from-[#F6FAF9] via-[#F2F9F7] to-[#EAF5F2] px-6 py-9 sm:px-10">
        <svg
          aria-hidden
          className="pointer-events-none absolute -right-10 top-0 h-full w-[320px]"
          viewBox="0 0 320 160"
          fill="none"
          preserveAspectRatio="none"
        >
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <path
              key={i}
              d={`M${40 + i * 34} 170C${90 + i * 30} 120 ${60 + i * 30} 60 ${140 + i * 26} -10`}
              stroke={TEAL}
              strokeOpacity="0.09"
              strokeWidth="1.2"
            />
          ))}
        </svg>

        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-8">
          <span className="flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-full bg-[#02857E] shadow-[0_14px_30px_-16px_rgba(2,133,126,0.75)] ring-8 ring-[#02857E]/[0.08]">
            <ShieldCheck className="h-8 w-8 text-white" strokeWidth={1.6} />
          </span>
          <div className="hidden h-[86px] w-px shrink-0 bg-[#D5E4E1] sm:block" />
          <div>
            <span className="text-[11.5px] font-bold uppercase tracking-[0.2em] text-[#0B6B62]">
              The LaneRunner difference
            </span>
            <p className="mt-3 max-w-3xl text-[21px] font-bold leading-[1.32] tracking-[-0.015em] text-[#0E1B1A] sm:text-[24px]">
              Private when you want control. Open when you want competition. Never a free-for-all.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HomeWhoItsFor;
